import { SearchCustomerBy4TResp } from '@ytl/common-model/lib/requests';
import { borgAnalytics, getChannel, useTheme } from '@ytl/common-web';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../../../contexts/languageContext';
import { OnboardingPhase, OnboardingStepperControls } from '../../../../modules/onboarding/OnboardingComponent';
import { VerificationComponent } from '../../../../modules/verificationComponent/VerificationComponent';
import { WidgetEvent } from '../../../@types/widgetEvents';
import { onProcessPhase, onWidgetEvent } from '../../../utils/emitData';
import { createUnsignedJWT } from '../../../utils/jwtUtils';

type VerificationProps = {
  stepper: OnboardingStepperControls;
  selectedSubscriber?: SearchCustomerBy4TResp;
  email?: string | null;
  customerDataJWT?: string | null;
  tmsVerification?: boolean;
};

export const Verification = ({
  stepper,
  selectedSubscriber,
  email,
  customerDataJWT,
  tmsVerification = false,
}: VerificationProps) => {
  const { translation } = useLanguage();
  const { isWebView } = useTheme();
  console.log(stepper);

  const [currentPhase, setCurrentPhase] = useState<string>('GetEmail');

  const initParamsJWT = useMemo(() => {
    const payload = {
      clientID: 'alm-client-001',
      sessionID: 'session-' + Date.now(),
      withPassword: true,
      captchaNeeded: false,
      EmailVerification: email ? false : true,
      TMSVerification: tmsVerification ? tmsVerification : false,
      MSISDNVerification: true,
      createDUP: true,
      salesMsisdn: '',
      salesCA: selectedSubscriber?.result.customerId || '',
      IPBanningEnabled: true,
      language: '',
      theme: '',
      additionalInfo: {
        application: isWebView ? 'APP' : 'ALM',
        channel_type: isWebView ? 'mobil_app' : 'web',
        process: 'Lakossági Yettel fiók regisztrálás',
        sales_type_cd: '7002',
        ca_id: selectedSubscriber?.result.customerId || '',
      },
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 3600,
    };
    return createUnsignedJWT(payload);
  }, [selectedSubscriber, email, tmsVerification, isWebView]);

  useEffect(() => {
    if (!email) {
      borgAnalytics.sendVirtualPageView({
        channel: getChannel(isWebView),
        virtualName: 'Create account',
      });
    }
  }, [email, isWebView]);

  useEffect(() => {
    const cleanup = onProcessPhase((phase: string) => {
      console.log('Verification component received phase change:', phase);
      setCurrentPhase(phase);
    });

    return cleanup;
  }, []);

  useEffect(() => {
    const cleanup = onWidgetEvent((data: any) => {
      switch (data.eventName) {
        case WidgetEvent.ProcessFinished:
          if (data.isSuccess) {
            setTimeout(() => {
              stepper.goToPhase(OnboardingPhase.Finish);
            }, 3000);
          } else {
            if (data.eventValue?.banned) {
              stepper.showBanned();
            } else {
              stepper.showError('GENERAL_ERROR');
            }
          }
          break;
        case WidgetEvent.TechnicalError:
          console.error('Technical error:', data.eventValue);
          break;
      }
    });

    return cleanup;
  }, [stepper]);

  const lastHeader = useRef('');
  const header = useMemo(() => {
    let result = '';
    if (currentPhase === 'GetEmail') {
      result = isWebView
        ? translation.onboarding.verificationFlow.header.registrationApp
        : translation.onboarding.verificationFlow.header.registration;
    } else if (currentPhase === 'EmailConfirm') {
      result = translation.onboarding.verificationFlow.header.emailVerification;
    } else if (currentPhase === 'SelectPhoneNumber' || currentPhase === 'AddDifferentPhoneNumber') {
      result = translation.onboarding.verificationFlow.header.phoneVerification;
    } else if (currentPhase === 'PhoneNumberConfirm') {
      result = translation.onboarding.verificationFlow.header.phoneVerification;
    }
    if (result) lastHeader.current = result;
    return lastHeader.current;
  }, [currentPhase, translation, isWebView]);

  const lastText = useRef('');
  const text = useMemo(() => {
    let result = '';
    if (currentPhase === 'GetEmail') {
      result = translation.onboarding.verificationFlow.text.enterEmail;
    } else if (currentPhase === 'EmailConfirm') {
      result = translation.onboarding.verificationFlow.text.emailVerification;
    } else if (currentPhase === 'SelectPhoneNumber' || currentPhase === 'AddDifferentPhoneNumber') {
      result = translation.onboarding.verificationFlow.text.phoneVerification;
    } else if (currentPhase === 'PhoneNumberConfirm') {
      result = translation.onboarding.verificationFlow.text.phoneCode;
    }
    if (result) lastText.current = result;
    return lastText.current;
  }, [currentPhase, translation]);

  return (
    <div className={`onboarding-step${isWebView ? ' onboarding-step--webview' : ''}`}>
      <h3
        className="onboarding-step__header"
        dangerouslySetInnerHTML={{ __html: header }}
      />
      <p
        className="onboarding-step__description"
        dangerouslySetInnerHTML={{ __html: text }}
      />
      <VerificationComponent
        initParamsJWT={initParamsJWT}
        initialEmail={email || ''}
        initialPhoneNumber={selectedSubscriber?.result?.phoneNumber}
        customerDataJWT={customerDataJWT}
        enableAnalytics
        selectedCA={selectedSubscriber?.result?.customerId}
      />
    </div>
  );
};
